import { findPdfGlyph, selectPdfGlyphs, type PdfTextGeometry } from './pdf-text-geometry';
import type { PdfMarkerRectangle } from './api.types';

export type PencilPage = {
  pageNumber: number;
  surface: HTMLElement;
  geometry: PdfTextGeometry;
};
export type PencilSelection = {
  pageNumber: number;
  text: string;
  rectangles: PdfMarkerRectangle[];
};
type Gesture = {
  source: 'pointer' | 'touch';
  id: number;
  page: PencilPage;
  anchor: number;
  head: number;
  x: number;
  y: number;
  moved: boolean;
};
type Options = {
  getPage: (x: number, y: number) => PencilPage | null;
  onChange: (selection: PencilSelection | null) => void;
  onTap: (x: number, y: number) => boolean;
  onLongPress: (x: number, y: number) => boolean;
};
export const PDF_MARKER_HOLD_DELAY = 600;
type StylusTouch = Touch & { touchType?: string };

export class PdfPencilSelection {
  public selection: PencilSelection | null = null;
  private gesture: Gesture | null = null;
  private holdTimer: number | null = null;
  private readonly listeners = new AbortController();

  public get isActive(): boolean { return this.gesture !== null; }
  public get ownsSelection(): boolean { return this.isActive || this.selection !== null; }

  public constructor(private readonly element: HTMLElement, private readonly options: Options) {
    const signal = this.listeners.signal;
    element.addEventListener('pointerdown', this.pointerDown, { signal });
    window.addEventListener('pointermove', this.pointerMove, { signal });
    window.addEventListener('pointerup', this.pointerUp, { signal });
    window.addEventListener('pointercancel', this.pointerCancel, { signal });
    // Safari exposes Pencil through both APIs. Touch owns the gesture once
    // identified, and its non-passive listener prevents native scroll takeover.
    element.addEventListener('touchstart', this.touchStart, { passive: false, signal });
    element.addEventListener('touchmove', this.touchMove, { passive: false, signal });
    element.addEventListener('touchend', this.touchEnd, { passive: false, signal });
    element.addEventListener('touchcancel', this.touchCancel, { signal });
    element.addEventListener('copy', this.copy, { signal });
    element.addEventListener('keydown', this.keyDown, { signal });
    window.addEventListener('blur', this.cancelActive, { signal });
  }

  public clear(): void {
    this.cancelHold();
    this.gesture = null;
    if (this.selection) {
      this.selection = null;
      this.options.onChange(null);
    }
  }

  public destroy(): void {
    this.listeners.abort();
    this.clear();
  }

  private readonly cancelActive = (): void => {
    if (this.isActive) this.clear();
  };

  private point(page: PencilPage, x: number, y: number): number | null {
    const bounds = page.surface.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0 ||
        x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) {
      return null;
    }
    return findPdfGlyph(page.geometry,
      (x - bounds.left) * page.geometry.width / bounds.width,
      (y - bounds.top) * page.geometry.height / bounds.height);
  }

  private begin(source: Gesture['source'], id: number, x: number, y: number): boolean {
    if (this.gesture) return false;
    const page = this.options.getPage(x, y);
    if (!page) return false;
    const anchor = this.point(page, x, y);
    if (anchor === null) return false;
    this.gesture = { source, id, page, anchor, head: anchor, x, y, moved: false };
    this.publish();
    this.holdTimer = window.setTimeout(() => {
      this.holdTimer = null;
      if (this.gesture && !this.gesture.moved && this.options.onLongPress(x, y)) this.clear();
    }, PDF_MARKER_HOLD_DELAY);
    document.getSelection()?.removeAllRanges();
    this.element.focus({ preventScroll: true });
    return true;
  }

  private update(x: number, y: number): void {
    const gesture = this.gesture;
    if (!gesture) return;
    gesture.moved ||= Math.hypot(x - gesture.x, y - gesture.y) > 6;
    if (gesture.moved) this.cancelHold();
    const head = this.point(gesture.page, x, y);
    if (head !== null && head !== gesture.head) {
      gesture.head = head;
      this.publish();
    }
  }

  private publish(): void {
    const gesture = this.gesture;
    if (!gesture) return;
    const { text, rectangles } = selectPdfGlyphs(gesture.page.geometry, gesture.anchor, gesture.head);
    this.selection = { pageNumber: gesture.page.pageNumber, text, rectangles };
    this.options.onChange(this.selection);
  }

  private finish(x: number, y: number): void {
    this.cancelHold();
    this.update(x, y);
    const tap = this.gesture && !this.gesture.moved;
    this.gesture = null;
    if (tap && this.options.onTap(x, y)) this.clear();
  }

  private cancelHold(): void {
    if (this.holdTimer !== null) window.clearTimeout(this.holdTimer);
    this.holdTimer = null;
  }

  private readonly pointerDown = (event: PointerEvent): void => {
    if (event.target instanceof Element && event.target.closest('button, select')) return;
    if (event.pointerType === 'pen' && event.button === 0 &&
        this.begin('pointer', event.pointerId, event.clientX, event.clientY)) {
      event.preventDefault();
    }
  };

  private readonly pointerMove = (event: PointerEvent): void => {
    if (this.gesture?.source === 'pointer' && this.gesture.id === event.pointerId) {
      this.update(event.clientX, event.clientY);
      if (event.cancelable) event.preventDefault();
    }
  };

  private readonly pointerUp = (event: PointerEvent): void => {
    if (this.gesture?.source === 'pointer' && this.gesture.id === event.pointerId) {
      this.finish(event.clientX, event.clientY);
      event.preventDefault();
    }
  };

  private readonly pointerCancel = (event: PointerEvent): void => {
    if (this.gesture?.source === 'pointer' && this.gesture.id === event.pointerId) this.clear();
  };

  private stylus(touches: TouchList): StylusTouch | undefined {
    return Array.from(touches).find(touch => (touch as StylusTouch).touchType === 'stylus');
  }

  private readonly touchStart = (event: TouchEvent): void => {
    if (event.target instanceof Element && event.target.closest('button, select')) return;
    const touch = this.stylus(event.changedTouches);
    if (!touch) {
      if (this.isActive && event.cancelable) event.preventDefault();
      return;
    }
    if (this.gesture?.source === 'pointer') {
      this.gesture.source = 'touch';
      this.gesture.id = touch.identifier;
    } else {
      this.begin('touch', touch.identifier, touch.clientX, touch.clientY);
    }
    if (this.isActive && event.cancelable) event.preventDefault();
  };

  private readonly touchMove = (event: TouchEvent): void => {
    if (!this.gesture) return;
    if (event.cancelable) event.preventDefault();
    if (this.gesture.source === 'touch') {
      const id = this.gesture.id;
      const touch = Array.from(event.changedTouches).find(candidate => candidate.identifier === id);
      if (touch) this.update(touch.clientX, touch.clientY);
    }
  };

  private readonly touchEnd = (event: TouchEvent): void => {
    if (this.gesture?.source !== 'touch') return;
    const id = this.gesture.id;
    const touch = Array.from(event.changedTouches).find(candidate => candidate.identifier === id);
    if (touch) {
      this.finish(touch.clientX, touch.clientY);
      if (event.cancelable) event.preventDefault();
    }
  };

  private readonly touchCancel = (event: TouchEvent): void => {
    if (this.gesture?.source === 'touch' &&
        Array.from(event.changedTouches).some(touch => touch.identifier === this.gesture?.id)) {
      this.clear();
    }
  };

  private readonly copy = (event: ClipboardEvent): void => {
    if (this.selection && event.clipboardData) {
      event.clipboardData.setData('text/plain', this.selection.text);
      event.preventDefault();
    }
  };

  private readonly keyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.ownsSelection) {
      this.clear();
      event.preventDefault();
      event.stopPropagation();
    }
  };
}
